"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

type Direction = "to-csv" | "to-lines";

const QUOTE_RE = /"/g;
const LEADING_SPACE_RE = /^\s*/;

function dedupeItems(items: string[]): string[] {
  return Array.from(new Set(items));
}

/** The text that separates items when splitting: the delimiter, trimmed, else a comma. */
function splitterOf(delimiter: string): string {
  return delimiter.trim().length > 0 ? delimiter.trim() : ",";
}

/**
 * One field as RFC 4180 writes it: wrapped in double quotes, with each quote inside
 * doubled. A field that holds the delimiter or a quote is wrapped even when quoting is
 * off, because a reader could not tell it from two fields (or a stray quote) otherwise.
 */
function formatField(item: string, splitter: string, forceQuote: boolean) {
  if (forceQuote || item.includes('"') || item.includes(splitter)) {
    return `"${item.replace(QUOTE_RE, '""')}"`;
  }
  return item;
}

export function linesToSeparated(
  text: string,
  delimiter: string,
  quote: boolean,
  dedupe: boolean
): { output: string; count: number } {
  let items = text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  if (dedupe) {
    items = dedupeItems(items);
  }
  const splitter = splitterOf(delimiter);
  items = items.map((item) => formatField(item, splitter, quote));
  return { output: items.join(delimiter), count: items.length };
}

/** Read a quoted field that opens at `start`; `""` is a quote, and a lone `"` closes it. */
function readQuoted(
  text: string,
  start: number
): { value: string; end: number } {
  let value = "";
  let i = start + 1;
  while (i < text.length) {
    if (text[i] !== '"') {
      value += text[i];
      i += 1;
    } else if (text[i + 1] === '"') {
      value += '"';
      i += 2;
    } else {
      return { value, end: i + 1 };
    }
  }
  return { value, end: i };
}

/** Split on the delimiter, honouring double-quoted fields (embedded delimiters, "" and line breaks). */
export function splitFields(text: string, splitter: string): string[] {
  const fields: string[] = [];
  let pos = 0;
  while (pos <= text.length) {
    const open =
      pos + (LEADING_SPACE_RE.exec(text.slice(pos))?.[0].length ?? 0);
    let value: string;
    let end: number;
    if (text[open] === '"') {
      const quoted = readQuoted(text, open);
      const next = text.indexOf(splitter, quoted.end);
      end = next < 0 ? text.length : next;
      value = quoted.value + text.slice(quoted.end, end).trim();
    } else {
      const next = text.indexOf(splitter, pos);
      end = next < 0 ? text.length : next;
      value = text.slice(pos, end).trim();
    }
    if (value.length > 0) {
      fields.push(value);
    }
    pos = end + splitter.length;
  }
  return fields;
}

function separatedToLines(
  text: string,
  delimiter: string
): { output: string; count: number } {
  const items = splitFields(text, splitterOf(delimiter));
  return { output: items.join("\n"), count: items.length };
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const COMMA_SEPARATOR_STRINGS = {
  direction: "Direction",
  linesToCommaSeparated: "Lines to comma-separated",
  commaSeparatedToLines: "Comma-separated to lines",
  options: "Options",
  delimiter: "Delimiter",
  wrapItemsInQuotes: "Wrap items in quotes",
  removeDuplicates: "Remove duplicates",
  inputOneItemPerLine: "Input — one item per line",
  inputSeparatedList: "Input — separated list",
  convert: "Convert",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  output: "Output",
  items: "Items",
  inputCharacters: "Input characters",
  outputCharacters: "Output characters",
  placeholderLines: "apple\nbanana\ncherry",
  placeholderCommaList: "apple, banana, cherry",
} as const;

export default function CommaSeparatorWidget() {
  const [input, setInput] = useState("");
  const [direction, setDirection] = useState<Direction>("to-csv");
  const [delimiter, setDelimiter] = useState(", ");
  const [quote, setQuote] = useState(false);
  const [dedupe, setDedupe] = useState(false);
  const [output, setOutput] = useState("");
  const [itemCount, setItemCount] = useState(0);
  const [copied, setCopied] = useState(false);

  function handleConvert() {
    if (input.trim().length === 0) {
      return;
    }
    const result =
      direction === "to-csv"
        ? linesToSeparated(input, delimiter, quote, dedupe)
        : separatedToLines(input, delimiter);
    setOutput(result.output);
    setItemCount(result.count);
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setItemCount(0);
    setCopied(false);
  }

  const strings = useToolUi(COMMA_SEPARATOR_STRINGS);

  const inputPlaceholder =
    direction === "to-csv"
      ? strings.placeholderLines
      : strings.placeholderCommaList;

  const fmt = useToolNumber();

  return (
    <>
      {/* Direction */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.direction}
        </p>
        <div className="flex flex-wrap gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={direction === "to-csv"}
              className="accent-primary"
              name="direction"
              onChange={() => setDirection("to-csv")}
              type="radio"
              value="to-csv"
            />
            {strings.linesToCommaSeparated}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={direction === "to-lines"}
              className="accent-primary"
              name="direction"
              onChange={() => setDirection("to-lines")}
              type="radio"
              value="to-lines"
            />
            {strings.commaSeparatedToLines}
          </label>
        </div>
      </section>
      {/* Options */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.options}</p>
        <div className="flex flex-wrap items-center gap-4">
          <label
            className="flex items-center gap-2 text-foreground text-sm"
            htmlFor="delimiter"
          >
            {strings.delimiter}
            <input
              className="w-24 rounded-lg border border-border bg-card px-3 py-2 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="delimiter"
              onChange={(e) => setDelimiter(e.target.value)}
              type="text"
              value={delimiter}
            />
          </label>
          {direction === "to-csv" && (
            <>
              <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
                <input
                  checked={quote}
                  className="accent-primary"
                  onChange={(e) => setQuote(e.target.checked)}
                  type="checkbox"
                />
                {strings.wrapItemsInQuotes}
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
                <input
                  checked={dedupe}
                  className="accent-primary"
                  onChange={(e) => setDedupe(e.target.checked)}
                  type="checkbox"
                />
                {strings.removeDuplicates}
              </label>
            </>
          )}
        </div>
      </section>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="comma-input"
        >
          {direction === "to-csv"
            ? strings.inputOneItemPerLine
            : strings.inputSeparatedList}
        </label>
        <textarea
          className="min-h-[180px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="comma-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={inputPlaceholder}
          value={input}
        />
      </div>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleConvert}
          type="button"
        >
          {strings.convert}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="comma-output"
          >
            {strings.output}
          </label>
          <textarea
            className="min-h-[180px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="comma-output"
            readOnly
            value={output}
          />
        </div>
      )}
      {/* Stats */}
      {output.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.items} value={fmt(itemCount)} />
          <StatCard label={strings.inputCharacters} value={fmt(input.length)} />
          <StatCard
            label={strings.outputCharacters}
            value={fmt(output.length)}
          />
        </div>
      )}
    </>
  );
}
